import React, { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, MessageSquare, Users, Flame, Facebook, Instagram, MessagesSquare, Globe, Loader2 } from "lucide-react";

const CHANNEL_META = {
    "Web Chat": { icon: Globe, color: "#0EA5E9", labelHe: "צ׳אט באתר", labelAr: "شات الموقع" },
    Facebook: { icon: Facebook, color: "#1877F2", labelHe: "מסנג׳ר", labelAr: "ماسنجر" },
    Instagram: { icon: Instagram, color: "#E1306C", labelHe: "אינסטגרם", labelAr: "إنستغرام" },
    WhatsApp: { icon: MessagesSquare, color: "#25D366", labelHe: "וואטסאפ", labelAr: "واتساب" },
};

const FunnelBar = ({ label, value, max, color, sublabel }) => {
    const pct = max > 0 ? Math.max(4, (value / max) * 100) : 0;
    return (
        <div className="flex items-center gap-3">
            <div className="w-20 shrink-0 text-xs font-semibold text-[#475569] text-right">{label}</div>
            <div className="flex-1 h-8 bg-[#F1F5F9] rounded-lg overflow-hidden relative">
                <div
                    className="h-full transition-all duration-500 flex items-center justify-end pl-3"
                    style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}dd, ${color})` }}
                >
                    <span className="text-white text-xs font-bold tabular-nums drop-shadow">{value}</span>
                </div>
            </div>
            {sublabel != null && (
                <div className="w-14 shrink-0 text-[11px] text-[#64748B] tabular-nums">{sublabel}</div>
            )}
        </div>
    );
};

const ChannelCard = ({ row, lang, max }) => {
    const meta = CHANNEL_META[row.channel] || CHANNEL_META["Web Chat"];
    const Icon = meta.icon;
    const label = lang === "ar" ? meta.labelAr : meta.labelHe;
    return (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-3" data-testid={`funnel-channel-${row.channel.replace(/\s/g, "-").toLowerCase()}`}>
            <div className="flex items-center gap-2">
                <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-white"
                    style={{ background: meta.color }}
                >
                    <Icon size={18} />
                </div>
                <div>
                    <div className="font-bold text-[#1A365D] text-sm">{label}</div>
                    <div className="text-[11px] text-[#64748B]">
                        {row.leadRate}% {lang === "ar" ? "تحويل → عميل" : "→ ליד"}
                        {" · "}
                        {row.hotRate}% {lang === "ar" ? "→ ساخن" : "→ חם"}
                    </div>
                </div>
            </div>
            <div className="space-y-1.5">
                <FunnelBar
                    label={lang === "ar" ? "محادثات" : "שיחות"}
                    value={row.sessions}
                    max={max}
                    color="#94A3B8"
                />
                <FunnelBar
                    label={lang === "ar" ? "عملاء" : "לידים"}
                    value={row.leads}
                    max={max}
                    color={meta.color}
                />
                <FunnelBar
                    label={lang === "ar" ? "ساخنون" : "חמים"}
                    value={row.hot}
                    max={max}
                    color="#EF4444"
                />
            </div>
        </div>
    );
};

const SummaryStat = ({ icon: Icon, value, label, color, sub, testId }) => (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-start gap-3" data-testid={testId}>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}22`, color }}>
            <Icon size={20} />
        </div>
        <div>
            <div className="text-2xl font-black text-[#0F172A] tabular-nums leading-none">{value}</div>
            <div className="text-xs text-[#64748B] mt-1">{label}</div>
            {sub != null && <div className="text-[11px] text-[#94A3B8] mt-0.5">{sub}</div>}
        </div>
    </div>
);

export default function AnalyticsFunnel({ lang, API, token, refreshKey }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await axios.get(`${API}/analytics/funnel`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!cancelled) setData(r.data);
            } catch (e) {
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [API, token, refreshKey]);

    if (loading) {
        return (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 mb-6 flex items-center gap-3 text-[#64748B]" data-testid="analytics-funnel-loading">
                <Loader2 size={16} className="animate-spin" />
                <span>{lang === "ar" ? "جارٍ تحميل التحليلات..." : "טוען אנליטיקס..."}</span>
            </div>
        );
    }
    if (!data) return null;

    const { channels, totals } = data;
    const maxSessions = Math.max(1, ...channels.map((c) => c.sessions));

    return (
        <div className="mb-6 space-y-4" data-testid="analytics-funnel">
            <div className="flex items-center gap-2">
                <TrendingUp size={20} className="text-[#D4AF37]" />
                <h3 className="font-serif text-lg font-bold text-[#1A365D]">
                    {lang === "ar" ? "قمع التحويل حسب القناة" : "משפך המרות לפי ערוץ"}
                </h3>
            </div>

            {/* Totals row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <SummaryStat
                    icon={MessageSquare}
                    value={totals.sessions}
                    label={lang === "ar" ? "إجمالي المحادثات" : "סה״כ שיחות"}
                    color="#0EA5E9"
                    testId="funnel-total-sessions"
                />
                <SummaryStat
                    icon={Users}
                    value={totals.leads}
                    label={lang === "ar" ? "عملاء محتملون" : "לידים"}
                    color="#D4AF37"
                    sub={`${totals.leadRate}% ${lang === "ar" ? "معدل التحويل" : "המרה"}`}
                    testId="funnel-total-leads"
                />
                <SummaryStat
                    icon={Flame}
                    value={totals.hot}
                    label={lang === "ar" ? "عملاء ساخنون" : "לידים חמים"}
                    color="#EF4444"
                    sub={`${totals.hotRate}% ${lang === "ar" ? "من العملاء" : "מהלידים"}`}
                    testId="funnel-total-hot"
                />
                <SummaryStat
                    icon={Globe}
                    value={
                        totals.sessions
                            ? Math.round(
                                ((channels.find((c) => c.channel === "Web Chat")?.sessions || 0) / totals.sessions) * 100
                            ) + "%"
                            : "—"
                    }
                    label={lang === "ar" ? "حصة الشات" : "חלק צ׳אט אתר"}
                    color="#1A365D"
                    testId="funnel-share-web"
                />
                <SummaryStat
                    icon={Facebook}
                    value={
                        (channels.find((c) => c.channel === "Facebook")?.sessions || 0) +
                        (channels.find((c) => c.channel === "Instagram")?.sessions || 0) +
                        (channels.find((c) => c.channel === "WhatsApp")?.sessions || 0)
                    }
                    label={lang === "ar" ? "من ميتا / وا" : "ממטא / וואטסאפ"}
                    color="#1877F2"
                    sub={lang === "ar" ? "ماسنجر · إنستا · واتساب" : "מסנג׳ר · אינסטה · WA"}
                    testId="funnel-share-meta"
                />
            </div>

            {/* Per-channel funnel cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {channels.map((row) => (
                    <ChannelCard key={row.channel} row={row} lang={lang} max={maxSessions} />
                ))}
            </div>
        </div>
    );
}
